// Components
import { Icon } from 'components/lib';
import { IconName } from 'components/lib/Icon';

// Top-level pages by route label, shared by the menu and the page header tiles. Where a feature also has a mobile tab, the symbol is the
// mobile one (Schedule = calendar, To-do = list-checks, Forms = clipboard-list, GPS = map-pin,
// Site Reporting = shield-alert), so both apps read as one product.
export const ROUTE_ICONS = {
  activityPage: 'activity',
  schedulePage: 'calendar',
  companiesPage: 'building-2',
  jobsPage: 'briefcase',
  employeesPage: 'users',
  formsPage: 'clipboard-list',
  checkpointsPage: 'shield-alert',
  gpsPage: 'map-pin',
  todoPage: 'list-checks',
  equipmentPage: 'wrench',
  mapViewPage: 'map',
  calendarPage: 'calendar-days',
  reportsPage: 'chart-column',
  invoicesPage: 'receipt',
  helpPage: 'circle-question-mark',
  rating: 'star',
  // Pages outside the menu that still show an icon tile in their header.
  payrollPage: 'wallet',
  integrationsPage: 'plug',
  paymentPage: 'credit-card',
  preferencesPage: 'sliders-horizontal',
  actionLogsPage: 'rotate-ccw-clock',
  feedbackPage: 'message-square-warning',
  implementationPlansPage: 'newspaper',
} as const satisfies Record<string, IconName>;

export type TRouteIconKey = keyof typeof ROUTE_ICONS;

/** The menu glyph for a route label; antd Menu sizes and spaces it as `.ant-menu-item-icon`. */
// Own keys only: a label such as "constructor" must not find Object.prototype's member
export const getMenuIcon = (label: string) =>
  Object.hasOwn(ROUTE_ICONS, label) ? (
    <Icon name={ROUTE_ICONS[label as TRouteIconKey]} size="sm" />
  ) : undefined;

// Second-level menu entries by sub-route label (the dropdown under a top-level page).
const SUB_ROUTE_ICONS = {
  employeeList: 'users',
  groups: 'users-round',
  log: 'history',
  leave: 'calendar-off',
  rates: 'dollar-sign',
  docs: 'file-text',
  todo: 'list-checks',
  companiesList: 'building-2',
  companiesLogins: 'key-round',
  companiesContacts: 'contact',
  companiesContactLogs: 'notebook-pen',
  companiesLocations: 'map-pin',
  companiesDocs: 'file-text',
  companiesLocRating: 'star',
  jobsList: 'briefcase',
  jobsGroups: 'layers',
  jobsRates: 'dollar-sign',
  jobLog: 'history',
  jobCandidates: 'user-plus',
  jobExpenses: 'receipt-text',
  checkpointsList: 'shield-check',
  checkpointScans: 'scan-line',
  parking: 'square-parking',
  incidents: 'siren',
  settings: 'settings',
  payments: 'credit-card',
  invoices: 'receipt',
  invoiceSettings: 'settings',
  invoicesRecurring: 'repeat',
  invoiceRegister: 'book-open',
  unbilledHours: 'hourglass',
  invoiceAging: 'calendar-clock',
  payrollSettings: 'settings',
  holidays: 'party-popper',
  payrollAdjustment: 'sliders-horizontal',
  laborBurdenLabels: 'tags',
  laborBurdenRates: 'percent',
  equipmentList: 'wrench',
  equipmentLog: 'history',
  equipmentExpenses: 'receipt-text',
  generalReport: 'clock',
  usageReport: 'chart-line',
  outsideGeofenceReport: 'map-pin-off',
  missingCheckinsReport: 'calendar-x',
  formResultsAnalysis: 'chart-pie',
  scheduleReport: 'calendar-clock',
  tardyReport: 'alarm-clock',
} as const satisfies Record<string, IconName>;

/** The dropdown glyph for a sub-route label; none for labels without one. */
export const getSubMenuIcon = (label: string) =>
  Object.hasOwn(SUB_ROUTE_ICONS, label) ? (
    <Icon name={SUB_ROUTE_ICONS[label as keyof typeof SUB_ROUTE_ICONS]} size="sm" />
  ) : undefined;

/** "New job", "New form" and the other create actions in the dropdowns. */
export const CREATE_ACTION_ICON = <Icon name="plus" size="sm" />;
